/**
 * useTagActions Hook
 * 
 * Handles tag operations: add/remove tags, get all tags, rename/delete tags
 * Tags replace the old single-category system with multi-select support.
 * First tag in the array is the "primary" tag (used for table grouping).
 * 
 * Backwards compatible: auto-migrates task.category → task.tags
 */

import { useMemo, useCallback } from 'react';
import { CATEGORY_COLORS } from './constants/colors';
import { getMostDistinctColor } from '../FlowchartView/utils/statusUtils';

/**
 * Get a distinct color for a new tag based on existing tag colors
 * @param {string} tagName - Name of the tag (used for consistent fallback)
 * @param {Array} existingTags - Existing tags with colors
 * @returns {string} Hex color
 */
const getColorForTag = (tagName, existingTags = []) => {
  const existingColors = existingTags
    .map(t => t.color)
    .filter(c => c && typeof c === 'string');
  
  return getMostDistinctColor(existingColors, CATEGORY_COLORS);
};

/**
 * Normalize task tags - handles backwards compatibility
 * Converts old task.category (string) to task.tags (array)
 * @param {Object} task - Task object
 * @returns {Array} Tags array
 */
export const normalizeTaskTags = (task) => {
  if (!task) return [];
  
  // If task already has tags array, use it
  if (Array.isArray(task.tags) && task.tags.length > 0) {
    return task.tags;
  }
  
  // Backwards compat: convert old category to tags
  if (task.category) {
    return [task.category];
  }
  
  return [];
};

/**
 * Get primary tag (first tag in array)
 * @param {Object} task - Task object
 * @returns {string|null} Primary tag name or null
 */
export const getPrimaryTag = (task) => {
  const tags = normalizeTaskTags(task);
  return tags.length > 0 ? tags[0] : null;
};

export const useTagActions = (localSprint, setLocalSprint, recordAction) => {

  /**
   * Add a tag to a task
   * If it's the first tag, it becomes the primary tag
   */
  const handleAddTag = useCallback((taskId, tagName) => {
    if (!tagName) return;
    
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    if (!task) return;
    
    const currentTags = normalizeTaskTags(task);
    
    // Don't add duplicate
    if (currentTags.includes(tagName)) return;
    
    const taskName = task.name || 'Task';
    const newTags = [...currentTags, tagName];

    setLocalSprint(prev => {
      if (!prev) return prev;
      
      let updatedTags = prev.tags || prev.categories || [];
      
      // Add tag to the tags array if it doesn't exist
      if (!updatedTags.find(t => t.name === tagName)) {
        updatedTags = [
          ...updatedTags,
          { name: tagName, color: getColorForTag(tagName, updatedTags) }
        ];
      }
      
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tags: updatedTags,
        categories: updatedTags, // Keep categories for backwards compat
        tasks: tasks.map(t => 
          t.id === taskId ? { ...t, tags: newTags, category: newTags[0], updatedAt: new Date().toISOString() } : t
        ),
      };
    });

    // Record for undo
    if (recordAction) {
      recordAction({
        type: 'ADD_TAG',
        description: `${taskName}: +${tagName}`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => 
                t.id === taskId ? { ...t, tags: currentTags, category: currentTags[0] || null } : t
              ),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            let updatedTags = prev.tags || prev.categories || [];
            if (!updatedTags.find(t => t.name === tagName)) {
              updatedTags = [
                ...updatedTags,
                { name: tagName, color: getColorForTag(tagName, updatedTags) }
              ];
            }
            return {
              ...prev,
              tags: updatedTags,
              categories: updatedTags,
              tasks: prev.tasks.map(t => 
                t.id === taskId ? { ...t, tags: newTags, category: newTags[0] } : t
              ),
            };
          });
        },
      });
    }
  }, [localSprint, setLocalSprint, recordAction]);

  /**
   * Remove a tag from a task
   */
  const handleRemoveTag = useCallback((taskId, tagName) => {
    if (!tagName) return;
    
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    if (!task) return;
    
    const currentTags = normalizeTaskTags(task);
    
    // Can't remove if not present
    if (!currentTags.includes(tagName)) return;
    
    const taskName = task.name || 'Task';
    const newTags = currentTags.filter(t => t !== tagName);

    setLocalSprint(prev => {
      if (!prev) return prev;
      
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tasks: tasks.map(t => 
          t.id === taskId ? { ...t, tags: newTags, category: newTags[0] || null, updatedAt: new Date().toISOString() } : t
        ),
      };
    });

    // Record for undo
    if (recordAction) {
      recordAction({
        type: 'REMOVE_TAG',
        description: `${taskName}: -${tagName}`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => 
                t.id === taskId ? { ...t, tags: currentTags, category: currentTags[0] || null } : t
              ),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => 
                t.id === taskId ? { ...t, tags: newTags, category: newTags[0] || null } : t
              ),
            };
          });
        },
      });
    }
  }, [localSprint, setLocalSprint, recordAction]);

  /**
   * Set all tags for a task (replaces existing tags)
   * First tag becomes primary
   */
  const handleSetTags = useCallback((taskId, tagNames) => {
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    if (!task) return;
    
    const currentTags = normalizeTaskTags(task);
    const newTags = Array.isArray(tagNames) ? tagNames : [];
    const taskName = task.name || 'Task';

    setLocalSprint(prev => {
      if (!prev) return prev;
      
      let updatedTags = prev.tags || prev.categories || [];
      
      // Add any new tags to the tags array
      for (const tagName of newTags) {
        if (!updatedTags.find(t => t.name === tagName)) {
          updatedTags = [
            ...updatedTags,
            { name: tagName, color: getColorForTag(tagName, updatedTags) }
          ];
        }
      }
      
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tags: updatedTags,
        categories: updatedTags,
        tasks: tasks.map(t => 
          t.id === taskId ? { ...t, tags: newTags, category: newTags[0] || null, updatedAt: new Date().toISOString() } : t
        ),
      };
    });

    // Record for undo
    if (recordAction) {
      const oldDisplay = currentTags.length > 0 ? currentTags.join(', ') : 'None';
      const newDisplay = newTags.length > 0 ? newTags.join(', ') : 'None';
      recordAction({
        type: 'SET_TAGS',
        description: `${taskName}: ${oldDisplay} → ${newDisplay}`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => 
                t.id === taskId ? { ...t, tags: currentTags, category: currentTags[0] || null } : t
              ),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            let updatedTags = prev.tags || prev.categories || [];
            for (const tagName of newTags) {
              if (!updatedTags.find(t => t.name === tagName)) {
                updatedTags = [
                  ...updatedTags,
                  { name: tagName, color: getColorForTag(tagName, updatedTags) }
                ];
              }
            }
            return {
              ...prev,
              tags: updatedTags,
              categories: updatedTags,
              tasks: prev.tasks.map(t => 
                t.id === taskId ? { ...t, tags: newTags, category: newTags[0] || null } : t
              ),
            };
          });
        },
      });
    }
  }, [localSprint, setLocalSprint, recordAction]);

  /**
   * Set primary tag (moves tag to first position)
   */
  const handleSetPrimaryTag = useCallback((taskId, tagName) => {
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    if (!task) return;
    
    const currentTags = normalizeTaskTags(task);
    
    // If tag not in list, add it as primary
    if (!currentTags.includes(tagName)) {
      handleSetTags(taskId, [tagName, ...currentTags]);
      return;
    }
    
    // Move tag to first position
    const newTags = [tagName, ...currentTags.filter(t => t !== tagName)];
    handleSetTags(taskId, newTags);
  }, [localSprint, handleSetTags]);

  // Derive all available tags from both the tags array AND task.tags values
  const allTags = useMemo(() => {
    if (!localSprint) return [];
    
    // Support both new 'tags' and old 'categories' field
    const savedTags = localSprint.tags || localSprint.categories || [];
    const savedTagNames = new Set(savedTags.map(t => t.name));
    
    // Find tags used in tasks that aren't in the saved tags array
    const taskTagNames = new Set();
    (localSprint.tasks || []).forEach(task => {
      const tags = normalizeTaskTags(task);
      tags.forEach(tag => {
        if (!savedTagNames.has(tag)) {
          taskTagNames.add(tag);
        }
      });
    });
    
    // Create tag objects for missing ones with distinct colors
    const derivedTags = [];
    const allExisting = [...savedTags];
    
    for (const name of taskTagNames) {
      const color = getColorForTag(name, allExisting);
      const newTag = { name, color };
      derivedTags.push(newTag);
      allExisting.push(newTag);
    }
    
    return [...savedTags, ...derivedTags];
  }, [localSprint]);

  const getAllTags = useCallback(() => allTags, [allTags]);

  const handleRenameTag = useCallback((oldName, newName) => {
    if (!oldName || !newName || oldName === newName) return;
    
    setLocalSprint(prev => {
      if (!prev) return prev;
      
      // Update the tag in the tags array
      const updatedTags = (prev.tags || prev.categories || []).map(tag => 
        tag.name === oldName ? { ...tag, name: newName } : tag
      );
      
      // Update all tasks that have this tag
      const updatedTasks = (prev.tasks || []).map(task => {
        const tags = normalizeTaskTags(task);
        if (tags.includes(oldName)) {
          const newTags = tags.map(t => t === oldName ? newName : t);
          return { ...task, tags: newTags, category: newTags[0] || null };
        }
        return task;
      });
      
      return {
        ...prev,
        tags: updatedTags,
        categories: updatedTags,
        tasks: updatedTasks,
      };
    });
  }, [setLocalSprint]);

  const handleDeleteTag = useCallback((tagName) => {
    if (!tagName) return;
    
    setLocalSprint(prev => {
      if (!prev) return prev;
      
      // Remove the tag from the tags array
      const updatedTags = (prev.tags || prev.categories || []).filter(tag => 
        tag.name !== tagName
      );
      
      // Remove tag from all tasks that have it
      const updatedTasks = (prev.tasks || []).map(task => {
        const tags = normalizeTaskTags(task);
        if (tags.includes(tagName)) {
          const newTags = tags.filter(t => t !== tagName);
          return { ...task, tags: newTags, category: newTags[0] || null };
        }
        return task;
      });
      
      return {
        ...prev,
        tags: updatedTags,
        categories: updatedTags,
        tasks: updatedTasks,
      };
    });
  }, [setLocalSprint]);

  // Legacy support: handleCategoryChange maps to handleSetTags with single tag
  // This maintains backwards compatibility with existing code
  const handleCategoryChange = useCallback((taskId, categoryName) => {
    if (categoryName) {
      // Get current tags and either add or set as primary
      const task = localSprint?.tasks?.find(t => t.id === taskId);
      const currentTags = task ? normalizeTaskTags(task) : [];
      
      if (currentTags.length === 0) {
        // No tags yet, just set this one
        handleSetTags(taskId, [categoryName]);
      } else {
        // Has tags, set this as primary (move to front)
        handleSetPrimaryTag(taskId, categoryName);
      }
    } else {
      // Clear all tags
      handleSetTags(taskId, []);
    }
  }, [localSprint, handleSetTags, handleSetPrimaryTag]);

  return {
    // New tag actions
    handleAddTag,
    handleRemoveTag,
    handleSetTags,
    handleSetPrimaryTag,
    handleRenameTag,
    handleDeleteTag,
    getAllTags,
    
    // Legacy support (maps to tag operations)
    handleCategoryChange,
    handleRenameCategory: handleRenameTag,
    handleDeleteCategory: handleDeleteTag,
    getAllCategories: getAllTags,
  };
};

export default useTagActions;

